// Общото за трите справки за резервации: статус → етикет и тон, минути и
// моменти като четим текст, датата на периода за заявката.
//
// Статусите са ПЕТ (docs/cross-repo-contract.md, инвариант 26) и етикетите
// им са същите като в модула „Резервации" — човек не бива да види „Неизпълнена"
// в един екран и друга дума за същото в справката.

import dayjs from "dayjs";
import type { TFunction } from "i18next";

import {
  EM_DASH,
  formatMinutes,
  type CellTone,
  type ChipCellValue,
} from "../../../../components/dataTable";
import type {
  ReservationReportStatus,
  ReservationStatusCounts,
} from "../../../../api/reportsReservations";

export const RESERVATION_STATUSES: ReservationReportStatus[] = [
  "upcoming",
  "seated",
  "completed",
  "cancelled",
  "expired",
];

const STATUS_TONE: Record<ReservationReportStatus, CellTone> = {
  upcoming: "info",
  seated: "brand",
  completed: "ok",
  cancelled: "neutral",
  expired: "danger",
};

export const statusLabel = (
  t: TFunction,
  status: ReservationReportStatus,
): string => t(`reportsPage.rvStatus_${status}`);

export const statusChip = (
  t: TFunction,
  status: ReservationReportStatus,
): ChipCellValue => ({
  tone: STATUS_TONE[status],
  label: statusLabel(t, status),
});

/** „42 мин" / „1 ч 16 мин"; `null` е тире, не нула. */
export const formatReservationMinutes = (
  t: TFunction,
  minutes: number | null | undefined,
): string =>
  formatMinutes(minutes, {
    hour: t("dataTable.hourShort"),
    minute: t("dataTable.minuteShort"),
  });

/** „28.09.2026 19:30" — датата винаги: справката често е за много дни. */
export const formatMomentWithDate = (iso: string | null | undefined): string => {
  if (!iso) return EM_DASH;
  const parsed = dayjs(iso);
  return parsed.isValid() ? parsed.format("DD.MM.YYYY HH:mm") : EM_DASH;
};

/** Само часът — за втория край на интервала („19:30 – 21:30"). */
export const formatHour = (iso: string | null | undefined): string => {
  if (!iso) return EM_DASH;
  const parsed = dayjs(iso);
  return parsed.isValid() ? parsed.format("HH:mm") : EM_DASH;
};

/** Забележката на резервацията за показване и Excel: `null` при липсваща или
 *  само от интервали (празното поле във формата не е забележка). */
export const reservationNoteText = (note: string | null | undefined): string | null =>
  note?.trim() || null;

/** Процент неявяване: неизпълнените спрямо всички, които не са отказани. */
export const noShowRate = (counts: ReservationStatusCounts): number | null => {
  const eligible = counts.reservations - counts.cancelled;
  return eligible > 0 ? (counts.expired / eligible) * 100 : null;
};

export const emptyStatusCounts = (): ReservationStatusCounts => ({
  reservations: 0,
  upcoming: 0,
  seated: 0,
  completed: 0,
  cancelled: 0,
  expired: 0,
});
